import { useQuery } from '@apollo/client';
import { Alert, Loader, Stack } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PaymentStatus } from './__generated__/graphql';
import { sumUpOrderDocument } from './features/checkout/graphql/queries';

type SumUpResponse =
  | {
      amount: number;
      checkout_reference: string;
      currency: string;
      description: string;
      id: string;
      merchant_code: string;
      status: 'PAID' | 'FAILED';
      transaction_code: string;
      transaction_id: string;
    }
  | { error_code: string; message: string; status: number };

declare global {
  interface Window {
    SumUpCard?: {
      mount: (config: {
        id: string;
        locale: string;
        checkoutId: string;
        onResponse: (type: string, body: SumUpResponse) => void;
      }) => void;
    };
  }
}

interface SumUpCard {
  mount: (config: {
    id: string;
    locale: string;
    checkoutId: string;
    onResponse: (type: string, body: SumUpResponse) => void;
  }) => void;
}

const SumUpPage = () => {
  const [response, setResponse] = useState<SumUpResponse>();

  const { orderId } = useParams();

  const { data, loading, refetch } = useQuery(sumUpOrderDocument, {
    variables: { orderId: orderId || '' },
    skip: !orderId,
    onCompleted: async (data) => {
      if (data?.sumupOrder.payments?.length !== 1) return;

      const waitForSumUpCard = () => {
        return new Promise((resolve) => {
          const interval = setInterval(() => {
            if (window.SumUpCard) {
              clearInterval(interval);
              resolve(window.SumUpCard);
            }
          }, 100); // Check every 100ms
        });
      };

      const sumup = (await waitForSumUpCard()) as SumUpCard;

      sumup.mount({
        id: 'sumup-card',
        locale: 'sk-SK',
        checkoutId: data.sumupOrder.payments[0].referenceId ?? '',
        onResponse: (_type, body) => {
          setResponse(body);
          setTimeout(() => refetch(), 2000);
        },
      });
    },
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://gateway.sumup.com/gateway/ecom/card/v2/sdk.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  if (loading) return <Loader />;

  if (data?.sumupOrder.payments?.at(0)?.status === PaymentStatus.Completed) {
    return (
      <Stack mt="md">
        <Alert color="green">Platba kartou bola úspešne prijatá. Ďakujeme!</Alert>
      </Stack>
    );
  }

  return (
    <Stack>
      {data?.sumupOrder.payments?.at(0)?.status === PaymentStatus.Failed ||
        (response?.status === 'FAILED' && (
          <Alert color="red">Platba kartou zlyhala. Skúste to prosím znova.</Alert>
        ))}
      {response && 'error_code' in response && (
        <Alert color="red">
          Platba kartou zlyhala. Skúste to prosím znova. ({response.error_code}) ({response.message}
          ) ( {response.status})
        </Alert>
      )}
      {response?.status === 'PAID' && (
        <Alert color="green">Platba kartou bola úspešne prijatá. Ďakujeme!</Alert>
      )}
      <div id="sumup-card"></div>
    </Stack>
  );
};

export default SumUpPage;
